import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useUser } from '~/atoms/auth';
import { useNecTranslations } from '~/hooks/useNecTranslations';
import { useTagManager, type ActionEventUser } from '~/utils/tagManager';

// `type` (et non `interface`) : assignable à Record<string, unknown> attendu par dataLayer.push
type PageEvent = {
    event: 'pageview';
    event_name: 'page_view';
    page: {
        page_language: 'fr' | 'en';
        page_category: string;
    };
    user: ActionEventUser | null;
};

/**
 * Composant qui envoie un événement pageview à chaque changement de route
 * Compatible Google Tag Manager
 */
export function PageViewEvent() {
    const location = useLocation();
    const { language } = useNecTranslations();
    const { getCategory } = useTagManager();
    const [user] = useUser();
    // Protection contre React Strict Mode (double effect call)
    const lastPathRef = useRef<string | null>(null);

    useEffect(() => {
        // Éviter les doublons si le pathname n'a pas changé
        if (lastPathRef.current === location.pathname) return;
        lastPathRef.current = location.pathname;

        pageViewEvent();
    }, [location.pathname]);

    function pageViewEvent() {
        if (typeof window === 'undefined' || !window.dataLayer) return;

        const event: PageEvent = {
            event: 'pageview',
            event_name: 'page_view',
            page: {
                page_language: language,
                page_category: getCategory(),
            },
            user: user
                ? {
                    user_id: user.usagerSAP.codeUtilisateur,
                    user_type: null,
                    user_account_count: null,
                    user_active_account_count: null,
                }
                : null,
        };

        window.dataLayer.push(event);
    }

    return null;
}
